import {
  resolveReimbursementRentalFieldsForApi,
  scopePayloadForReimbursementPage,
} from '@/features/resident/utils/detailPayloadScope'

type RentalFieldSource = Parameters<typeof resolveReimbursementRentalFieldsForApi>[0]
import type { ReimbursementsDetailValues } from '../types/schemas'

export function buildReimbursementUpdatePayload(
  values: ReimbursementsDetailValues,
  applicationId: string | number,
  isLibertyRole: boolean,
  options?: { claimOverride?: boolean },
): Record<string, unknown> {
  const baseFields: Record<string, unknown> = {
    // Angular PR #1170: reimbursement detail saves use page:'default'.
    page: 'default',
    application_id: applicationId,
    ...values,
  }
  baseFields.send_to = isLibertyRole ? 'property' : 'liberty'
  // Angular `claim_override=1` after Liberty confirms first-month out-of-lease override.
  if (options?.claimOverride) {
    baseFields.claim_override = '1'
  }
  baseFields.is_final_ledger = values.is_final_ledger ? '1' : ''
  delete baseFields.lease_agreement
  delete baseFields.payment_ledger
  delete baseFields.eviction_lawsuit
  delete baseFields.skip_claim_affidavit
  delete baseFields.cdc_affidavit
  delete baseFields.jurisdiction_eviction_updates
  delete baseFields.other_supporting_documents
  if (values.first_month_not_paid_month && values.first_month_not_paid_year) {
    baseFields.first_month_not_paid = `${values.first_month_not_paid_year}-${String(
      values.first_month_not_paid_month,
    ).padStart(2, '0')}-01`
  }
  const lossAdjustedReason = (baseFields.loss_adjusted_reason as unknown[] | undefined) ?? []
  if (Array.isArray(lossAdjustedReason) && lossAdjustedReason.length > 0) {
    lossAdjustedReason.forEach((reason, index) => {
      baseFields[`loss_adjusted_reason[${index}]`] = reason
    })
  } else {
    baseFields['loss_adjusted_reason[]'] = []
  }
  delete baseFields.loss_adjusted_reason
  if (['NOT_STARTED', 'ACTIVE'].includes(values.rental_status)) {
    delete baseFields.covid_19
  }
  delete baseFields.message
  scopePayloadForReimbursementPage(baseFields, values as RentalFieldSource)
  return baseFields
}

export function buildReimbursementFileUploadFormData(
  applicationId: string | number,
  pendingFiles: {
    lease_agreement?: File
    payment_ledger?: File
    eviction_lawsuit?: File[]
    skip_claim_affidavit?: File
  },
  options?: { isFinalLedger?: boolean },
): FormData {
  const fd = new FormData()
  // Angular PR #1170: reimbursement detail uploads use page:'default'.
  fd.append('page', 'default')
  fd.append('application_id', String(applicationId))
  if (pendingFiles.lease_agreement) fd.append('lease_agreement', pendingFiles.lease_agreement)
  if (pendingFiles.payment_ledger) fd.append('payment_ledger', pendingFiles.payment_ledger)
  if (pendingFiles.skip_claim_affidavit) fd.append('skip_claim_affidavit', pendingFiles.skip_claim_affidavit)
  pendingFiles.eviction_lawsuit?.forEach((f, i) => fd.append(`eviction_lawsuit[${i}]`, f))
  // Angular uploadFile(payment_ledger): Yes on Final Ledger dialog sets is_final_ledger='1' on the POST.
  if (options?.isFinalLedger && pendingFiles.payment_ledger) {
    fd.append('is_final_ledger', '1')
  }
  return fd
}
